import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { ProfileRejectPayload } from '../../config/profileRejectionReasons';
import {
  getUsers,
  getCreatorSocialData,
  getUserById,
  modifyUserStatus,
  refreshCreatorSocialScore,
} from '../../services/adminService';
import {
  fetchAllUsersAndLinks,
  getAdminAnalytics,
} from '../../services/userService';

const STALE_TIME = 30_000;

export const useGetUsersQuery = (
  status: string,
  page: number,
  limit: number,
  searchTerm: string,
  filterOption: string,
) =>
  useQuery({
    queryKey: queryKeys.admin.users({
      status,
      page,
      limit,
      searchTerm,
      filterOption,
    }),
    queryFn: () =>
      getUsers({
        status: status.toLowerCase(),
        page,
        limit,
        search: searchTerm || undefined,
        duration: filterOption === 'all-time' ? undefined : filterOption,
      }),
    staleTime: STALE_TIME,
  });

export const useGetAllUsersAndLinksQuery = () =>
  useQuery({
    queryKey: queryKeys.admin.allUsersAndLinks(),
    queryFn: () =>
      fetchAllUsersAndLinks({
        filter: '',
        page: 1,
        limit: 1000,
        sortField: '_id',
        sortOrder: 'desc',
      }),
    staleTime: STALE_TIME,
  });

export const useGetCreatorSocialDataQuery = (userId: string) =>
  useQuery({
    queryKey: queryKeys.admin.userProfile(userId),
    queryFn: () => getCreatorSocialData(userId),
    staleTime: STALE_TIME,
    enabled: !!userId,
  });

export const useGetUserByIdQuery = (userId: string) =>
  useQuery({
    queryKey: queryKeys.admin.userById(userId),
    queryFn: () => getUserById(userId),
    staleTime: STALE_TIME,
    enabled: !!userId,
  });

export const useModifyUserStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      status,
      rejection,
    }: {
      userId: string;
      status: string;
      rejection?: ProfileRejectPayload;
    }) => modifyUserStatus(userId, status, rejection),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'users'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.userProfile(userId),
      });
    },
  });
};

export const useRefreshSocialScoreMutation = () =>
  useMutation({
    mutationFn: (userId: string) => refreshCreatorSocialScore(userId),
  });

export const useAdminAnalyticsQuery = () =>
  useQuery({
    queryKey: queryKeys.admin.adminAnalytics(),
    queryFn: () => getAdminAnalytics(),
    staleTime: STALE_TIME,
  });

export const useGetAllUsersAndLinksPagedQuery = (
  filter: string,
  page: number,
  limit: number,
  sortField: string,
  sortOrder: string,
) =>
  useQuery({
    queryKey: queryKeys.admin.allUsersAndLinksPaged(
      filter,
      page,
      limit,
      sortField,
      sortOrder,
    ),
    queryFn: () =>
      fetchAllUsersAndLinks({ filter, page, limit, sortField, sortOrder }),
    staleTime: STALE_TIME,
  });
